import React, { useState, useEffect } from 'react';
import { Leaf, Droplet, Calendar, CheckCircle, Users, Star, ArrowRight, Play, X } from 'lucide-react';

const LandingPage = ({ onGetStarted, onLogin, onTryDemo }) => {
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [email, setEmail] = useState('');

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !localStorage.getItem('exitShown')) {
        setShowExitPopup(true);
        localStorage.setItem('exitShown', 'true');
        // Reset after 24 hours
        setTimeout(() => localStorage.removeItem('exitShown'), 24 * 60 * 60 * 1000);
      }
    };
    
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  const handleQuickSignup = () => {
    if (email) {
      onGetStarted(email);
    }
  };

  // Track landing page view
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: 'Landing Page',
        page_location: window.location.href
      });
    }
  }, []);

  return (
    <>
      {/* Hero Section */}
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        {/* Navigation */}
        <nav className="bg-white/80 backdrop-blur-sm border-b border-green-100 sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center">
              <Leaf className="text-green-600 mr-2" size={28} />
              <h1 className="text-xl font-bold text-gray-800">PlantCareTracker</h1>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onLogin}
                className="px-4 py-2 text-gray-700 hover:text-green-600 font-medium transition"
              >
                Login
              </button>
              <button
                onClick={() => onGetStarted()}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition shadow-sm"
              >
                Get Started Free
              </button>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="max-w-6xl mx-auto px-4 pt-16 pb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Star size={16} fill="currentColor" />
              <span>Join 500+ plant parents keeping their plants alive</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Never Let Your<br />
              <span className="text-green-600">Plants Die</span> Again
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Smart watering reminders, care tracking, and growth notes. 
              Everything you need to become a successful plant parent.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <button
                onClick={onTryDemo}
                className="w-full sm:w-auto px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-lg transition transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
              >
                <Play size={20} />
                Try Demo Instantly
              </button>
              <button
                onClick={() => onGetStarted()}
                className="w-full sm:w-auto px-8 py-4 bg-white hover:bg-gray-50 text-green-600 border-2 border-green-600 rounded-lg font-semibold text-lg transition flex items-center justify-center gap-2"
              >
                Create Free Account
                <ArrowRight size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-500">
              ✨ No credit card required • 🚀 Setup in 30 seconds • 💚 Free forever
            </p>
          </div>

          {/* Quick Signup Form */}
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-xl p-6 mb-16">
            <h3 className="text-lg font-semibold mb-4 text-center">Start Tracking Your Plants Now</h3>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Enter your email..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleQuickSignup()}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <button
                onClick={handleQuickSignup}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition whitespace-nowrap"
              >
                Get Started
              </button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Droplet className="text-blue-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Smart Watering Reminders</h3>
              <p className="text-gray-600">
                Personalized schedules for each plant. Never underwater or overwater again.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Calendar className="text-green-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Care Tracking</h3>
              <p className="text-gray-600">
                Log watering, fertilizing, and care notes. Watch your plants thrive over time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="text-purple-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Multi-User Support</h3>
              <p className="text-gray-600">
                Perfect for families. Everyone can help care for the plants together.
              </p>
            </div>
          </div>

          {/* Social Proof */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
            <h3 className="text-2xl font-bold text-center mb-8">Plant Parents Love PlantCareTracker</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="text-yellow-400" fill="currentColor" />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-3">
                  "Finally, an app that reminds me when to water my succulents! My plants have never been healthier."
                </p>
                <p className="font-semibold">- Sarah M.</p>
              </div>
              
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="text-yellow-400" fill="currentColor" />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-3">
                  "Simple, beautiful, and it works! I've kept my plants alive for 6 months now - a personal record!"
                </p>
                <p className="font-semibold">- David L.</p>
              </div>
              
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="text-yellow-400" fill="currentColor" />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-3">
                  "The care notes feature is amazing. I can track what works for each plant. Highly recommend!"
                </p>
                <p className="font-semibold">- Emma R.</p>
              </div>
            </div>
          </div>

          {/* Benefits List */}
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-center mb-8">Everything You Need to Succeed</h3>
            <div className="space-y-4">
              {[
                "Unlimited plants - track your entire collection",
                "Custom watering schedules for each plant type",
                "Growth tracking with notes and observations",
                "Mobile-friendly - check plants on the go",
                "Reminder notifications (coming soon!)",
                "Plant care guides and tips (coming soon!)"
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                  <span className="text-lg text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center mt-16">
            <h2 className="text-3xl font-bold mb-4">Ready to Save Your Plants?</h2>
            <p className="text-xl text-gray-600 mb-8">
              Join hundreds of plant parents who never forget to water again.
            </p>
            <button
              onClick={() => onGetStarted()}
              className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-lg transition transform hover:scale-105 shadow-lg"
            >
              Start Free - No Card Required
            </button>
          </div>
        </div>
      </div>

      {/* Exit Intent Popup */}
      {showExitPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowExitPopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
            
            <div className="text-center">
              <Leaf className="text-green-600 mx-auto mb-4" size={48} />
              <h2 className="text-2xl font-bold mb-3">Wait! Don't Let Your Plants Die 🌱</h2>
              <p className="text-gray-600 mb-6">
                Try our demo first - no signup required! See how easy it is to keep your plants healthy.
              </p>
              
              <button
                onClick={() => {
                  setShowExitPopup(false);
                  onTryDemo();
                }}
                className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold mb-3 transition"
              >
                Try Demo Now (No Signup)
              </button>
              
              <button
                onClick={() => setShowExitPopup(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                No thanks, I'll let them die
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingPage;